import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { FileItem, ViewMode, BreadcrumbItem } from '@/components/file-manager/types';
import { SupabaseFileStorage } from '@/components/file-manager/utils/supabaseFileStorage';
import { Toolbar, ViewControls } from '@/components/file-manager/components/Toolbar';
import { FileGrid } from '@/components/file-manager/components/FileGrid';
import { UploadModal } from '@/components/file-manager/components/UploadModal';
import { CreateFolderModal } from '@/components/file-manager/components/CreateFolderModal';
import { FolderSelectionModal } from '@/components/file-manager/components/FolderSelectionModal';
import { FilePreviewModal } from '@/components/file-manager/components/FilePreviewModal';
import { RenameModal } from '@/components/file-manager/components/RenameModal';
import { DetailsModal } from '@/components/file-manager/components/DetailsModal';
import { UploadProgressPopup, UploadProgress } from '@/components/file-manager/components/UploadProgressPopup';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { supabase } from '@/lib/supabase';
import { uploadToR2 } from '@/lib/r2-storage';
import { fileManagerPreloader } from '@/components/file-manager/utils/fileManagerPreloader';
import { useAuth } from '@/context/AuthContext';
import { useWorkspace } from '@/context/WorkspaceContext';
import { Lock } from 'lucide-react';
import { toast } from 'sonner';

interface ProjectFileTabProps {
  projectId: string;
  projectName: string;
  /** true = client with view-only access; false = agency/admin with full edit access */
  isReadOnly: boolean;
  isGlobalDarkMode: boolean;
}

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Find the project root folder in workspace_files.
 * Uses limit(1) ordered by created_at so it never errors on duplicates.
 */
async function findProjectRootFolder(
  workspaceId: string,
  projectId: string,
  projectName?: string
): Promise<FileItem | null> {
  try {
    // 1. Try finding via project.folder_id
    const { data: proj } = await supabase
      .from('projects')
      .select('folder_id')
      .eq('id', projectId)
      .maybeSingle();

    if (proj?.folder_id) {
      const { data: fData } = await supabase
        .from('workspace_files')
        .select('*')
        .eq('id', proj.folder_id)
        .eq('trashed', false)
        .maybeSingle();

      if (fData) {
        if (fData.project_id !== projectId) {
          await supabase
            .from('workspace_files')
            .update({ project_id: projectId })
            .eq('id', fData.id);
        }

        return {
          id: fData.id,
          name: fData.name,
          type: 'folder',
          size: fData.size,
          mimeType: fData.mime_type,
          parentId: fData.parent_id || undefined,
          projectId: projectId,
          createdAt: new Date(fData.created_at),
          modifiedAt: new Date(fData.updated_at),
          starred: fData.starred || false,
          shared: fData.shared || false,
          url: fData.url,
          trashed: false,
          ownerId: fData.uploaded_by,
          ownerName: fData.owner_name,
          ownerAvatarUrl: fData.owner_avatar_url,
          modifiedBy: fData.modified_by,
        };
      }
    }

    // 2. Try finding by project_id in workspace_files
    const { data, error } = await supabase
      .from('workspace_files')
      .select('*')
      .eq('workspace_id', workspaceId)
      .eq('project_id', projectId)
      .eq('type', 'folder')
      .eq('trashed', false)
      .order('created_at', { ascending: true });

    if (!error && data && data.length > 0) {
      const row = data[0];

      // Update project.folder_id if missing
      await supabase.from('projects').update({ folder_id: row.id }).eq('id', projectId);

      // Clean up empty duplicate folders if any exist
      if (data.length > 1) {
        const duplicateIds = data.slice(1).map(d => d.id);
        supabase.from('workspace_files').update({ trashed: true }).in('id', duplicateIds).then(() => {}, console.error);
      }

      return {
        id: row.id,
        name: row.name,
        type: 'folder',
        size: row.size,
        mimeType: row.mime_type,
        parentId: row.parent_id || undefined,
        projectId: row.project_id,
        createdAt: new Date(row.created_at),
        modifiedAt: new Date(row.updated_at),
        starred: row.starred || false,
        shared: row.shared || false,
        url: row.url,
        trashed: false,
        ownerId: row.uploaded_by,
        ownerName: row.owner_name,
        ownerAvatarUrl: row.owner_avatar_url,
        modifiedBy: row.modified_by,
      };
    }

    // 3. Try finding an existing folder with the same name
    if (projectName) {
      const { data: matchData } = await supabase
        .from('workspace_files')
        .select('*')
        .eq('workspace_id', workspaceId)
        .eq('name', projectName)
        .eq('type', 'folder')
        .eq('trashed', false)
        .order('created_at', { ascending: true });

      if (matchData && matchData.length > 0) {
        const row = matchData[0];
        // Link it to the project
        await Promise.all([
          supabase.from('workspace_files').update({ project_id: projectId }).eq('id', row.id),
          supabase.from('projects').update({ folder_id: row.id }).eq('id', projectId)
        ]);

        if (matchData.length > 1) {
          const duplicateIds = matchData.slice(1).map(d => d.id);
          supabase.from('workspace_files').update({ trashed: true }).in('id', duplicateIds).then(() => {}, console.error);
        }

        return {
          id: row.id,
          name: row.name,
          type: 'folder',
          size: row.size,
          mimeType: row.mime_type,
          parentId: row.parent_id || undefined,
          projectId: projectId,
          createdAt: new Date(row.created_at),
          modifiedAt: new Date(row.updated_at),
          starred: row.starred || false,
          shared: row.shared || false,
          url: row.url,
          trashed: false,
          ownerId: row.uploaded_by,
          ownerName: row.owner_name,
          ownerAvatarUrl: row.owner_avatar_url,
          modifiedBy: row.modified_by,
        };
      }
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Create the project root folder and tag it with project_id.
 */
async function createProjectRootFolder(
  workspaceId: string,
  projectId: string,
  projectName: string,
  userId: string,
  ownerName: string,
  ownerAvatarUrl?: string | null
): Promise<FileItem | null> {
  try {
    // Check if folder already exists before creating
    const existing = await findProjectRootFolder(workspaceId, projectId, projectName);
    if (existing) {
      return existing;
    }

    let projectsParentId: string | null = null;
    const { data: existingProjectsFolder } = await supabase
      .from('workspace_files')
      .select('id')
      .eq('workspace_id', workspaceId)
      .eq('name', 'Projects')
      .eq('type', 'folder')
      .is('parent_id', null)
      .eq('trashed', false)
      .maybeSingle();

    if (existingProjectsFolder) {
      projectsParentId = existingProjectsFolder.id;
    } else {
      const { data: newProjectsFolder } = await supabase
        .from('workspace_files')
        .insert({
          workspace_id: workspaceId,
          name: 'Projects',
          type: 'folder',
          parent_id: null,
          size: 0,
          starred: false,
          shared: false,
          trashed: false,
          uploaded_by: userId,
        })
        .select('id')
        .single();
      if (newProjectsFolder) {
        projectsParentId = newProjectsFolder.id;
      }
    }

    const { data, error } = await supabase
      .from('workspace_files')
      .insert({
        workspace_id: workspaceId,
        project_id: projectId,
        name: projectName,
        type: 'folder',
        parent_id: projectsParentId,
        starred: false,
        shared: false,
        trashed: false,
        uploaded_by: userId,
        owner_name: ownerName,
        owner_avatar_url: ownerAvatarUrl,
        modified_by: ownerName,
      })
      .select()
      .single();

    if (error) throw error;

    // Link folder_id to project
    await supabase.from('projects').update({ folder_id: data.id }).eq('id', projectId);

    return {
      id: data.id,
      name: data.name,
      type: 'folder',
      parentId: data.parent_id || undefined,
      projectId: projectId,
      createdAt: new Date(data.created_at),
      modifiedAt: new Date(data.updated_at),
      starred: false,
      shared: false,
      trashed: false,
      ownerId: data.uploaded_by,
      ownerName: data.owner_name,
      ownerAvatarUrl: data.owner_avatar_url,
      modifiedBy: data.modified_by,
    };
  } catch (err) {
    console.error('Error creating project root folder:', err);
    return null;
  }
}

// ─── component ──────────────────────────────────────────────────────────────

export const ProjectFileTab: React.FC<ProjectFileTabProps> = ({
  projectId,
  projectName,
  isReadOnly,
  isGlobalDarkMode,
}) => {
  const { profile, user } = useAuth();
  const { currentWorkspace } = useWorkspace();

  // All workspace files (shared source of truth with the file manager)
  const [allFiles, setAllFiles] = useState<FileItem[]>([]);
  const [loading, setLoading] = useState(true);

  // The project root folder ID — once resolved we navigate into it
  const [projectRootId, setProjectRootId] = useState<string | null>(null);
  // Current folder being viewed (starts at project root)
  const [currentFolderId, setCurrentFolderId] = useState<string | undefined>();

  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>({ type: 'grid' });
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set());
  const [selectionMode, setSelectionMode] = useState(false);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [itemToMove, setItemToMove] = useState<string | null>(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [itemToRename, setItemToRename] = useState<FileItem | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [itemDetails, setItemDetails] = useState<FileItem | null>(null);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const currentUserName = useMemo(() => {
    if (profile?.first_name || profile?.last_name)
      return `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 'User';
    return profile?.email?.split('@')[0] || 'User';
  }, [profile]);

  const currentUserAvatarUrl = profile?.avatar_url || null;

  // ── Load all workspace files (shared with file manager) ──────────────────
  const loadFiles = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      const data = await SupabaseFileStorage.getFiles(currentWorkspace.id);
      setAllFiles(data);
    } catch (err) {
      console.error('Error loading files:', err);
    }
  }, [currentWorkspace]);

  // ── Initialise: find/create project root folder then load files ───────────
  useEffect(() => {
    if (!currentWorkspace || !user) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        // 1. Find or create project root folder
        let root = await findProjectRootFolder(currentWorkspace.id, projectId, projectName);
        if (!root) {
          root = await createProjectRootFolder(
            currentWorkspace.id,
            projectId,
            projectName,
            user.id,
            currentUserName,
            currentUserAvatarUrl
          );
          // Invalidate preloader cache so the file manager shows the new folder
          fileManagerPreloader.invalidate(currentWorkspace.id);
        }

        if (cancelled) return;
        if (root) {
          setProjectRootId(root.id);
          setCurrentFolderId(root.id);
        }

        // 2. Load all workspace files (via preloader cache when available)
        const cached = fileManagerPreloader.getCached(currentWorkspace.id);
        if (cached) {
          setAllFiles(cached.files);
          setLoading(false);
          // Refresh in background
          fileManagerPreloader.preload(currentWorkspace.id).then(() => {
            if (cancelled) return;
            const updated = fileManagerPreloader.getCached(currentWorkspace.id);
            if (updated) setAllFiles(updated.files);
          }).catch(console.error);
        } else {
          const data = await SupabaseFileStorage.getFiles(currentWorkspace.id);
          if (!cancelled) setAllFiles(data);
          setLoading(false);
          fileManagerPreloader.preload(currentWorkspace.id).catch(console.error);
        }
      } catch (err) {
        console.error(err);
        setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [currentWorkspace?.id, projectId]);

  // ── File list visible in current folder ──────────────────────────────────
  const currentFiles = useMemo(() => {
    let files = allFiles.filter(f => !f.trashed && f.parentId === currentFolderId);

    if (fileTypeFilter !== 'all') {
      files = files.filter(f => {
        if (f.type === 'folder') return true;
        const m = f.mimeType?.toLowerCase() || '';
        if (fileTypeFilter === 'images') return m.includes('image/');
        if (fileTypeFilter === 'documents')
          return m.includes('pdf') || m.includes('document') || m.includes('text');
        return true;
      });
    }

    if (searchQuery) {
      files = files.filter(f => f.name.toLowerCase().includes(searchQuery.toLowerCase()));
    }

    return files;
  }, [allFiles, currentFolderId, searchQuery, fileTypeFilter]);

  // ── Breadcrumbs (from project root, not workspace root) ──────────────────
  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    if (!projectRootId) return [];
    const crumbs: BreadcrumbItem[] = [];
    let fid = currentFolderId;

    while (fid && fid !== projectRootId) {
      const folder = allFiles.find(f => f.id === fid);
      if (!folder) break;
      crumbs.unshift({ id: folder.id, name: folder.name });
      fid = folder.parentId;
    }
    return crumbs;
  }, [allFiles, currentFolderId, projectRootId]);

  // ── Refresh helper (invalidates preloader so file manager also updates) ───
  const refresh = useCallback(async () => {
    if (!currentWorkspace) return;
    fileManagerPreloader.invalidate(currentWorkspace.id);
    const data = await SupabaseFileStorage.getFiles(currentWorkspace.id);
    setAllFiles(data);
  }, [currentWorkspace]);

  // ── Realtime subscription — keep in sync with File Manager changes ────────
  useEffect(() => {
    if (!currentWorkspace) return;

    const channel = supabase
      .channel(`project_files:${currentWorkspace.id}:${projectId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'workspace_files',
          filter: `workspace_id=eq.${currentWorkspace.id}`,
        },
        () => {
          loadFiles();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentWorkspace?.id, projectId, loadFiles]);

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleFolderOpen = (folderId: string) => {
    setSelectionMode(false);
    setSelectedItems(new Set());
    setCurrentFolderId(folderId);
  };

  const handleGoToRoot = () => {
    setSelectionMode(false);
    setSelectedItems(new Set());
    setCurrentFolderId(projectRootId ?? undefined);
  };

  const handleFileDelete = async (id: string) => {
    await SupabaseFileStorage.deleteFile(id);
    await refresh();
  };

  const handleFileRestore = async (id: string) => {
    await SupabaseFileStorage.restoreFile(id);
    await refresh();
  };

  const handlePermanentDelete = async (id: string) => {
    await SupabaseFileStorage.permanentlyDeleteFile(id);
    await refresh();
  };

  const handleToggleStar = async (id: string) => {
    const file = allFiles.find(f => f.id === id);
    if (file) {
      await SupabaseFileStorage.updateFile(id, { starred: !file.starred, modifiedBy: currentUserName });
      await refresh();
    }
  };

  const handleShare = async (id: string) => {
    const file = allFiles.find(f => f.id === id);
    if (file) {
      await SupabaseFileStorage.updateFile(id, { shared: !file.shared, modifiedBy: currentUserName });
      await refresh();
    }
  };

  const handleDuplicate = async (id: string) => {
    if (!currentWorkspace || !user) return;
    const file = allFiles.find(f => f.id === id);
    if (!file) return;
    const dup = { ...file, name: `${file.name} - Copy`, starred: false, shared: false };
    delete (dup as any).id; delete (dup as any).createdAt; delete (dup as any).modifiedAt;
    await SupabaseFileStorage.addFile(currentWorkspace.id, user.id, dup);
    await refresh();
  };

  const handleMoveTo = (id: string) => { setItemToMove(id); setShowMoveModal(true); };
  const handleRename = (id: string) => {
    const item = allFiles.find(f => f.id === id);
    if (item) { setItemToRename(item); setShowRenameModal(true); }
  };
  const handleFileDetails = (file: FileItem) => { setItemDetails(file); setShowDetailsModal(true); };
  const handleFolderDetails = (folder: FileItem) => { setItemDetails(folder); setShowDetailsModal(true); };

  const handleRenameConfirm = async (newName: string) => {
    if (!itemToRename || !newName.trim()) return;
    await SupabaseFileStorage.updateFile(itemToRename.id, { name: newName.trim(), modifiedBy: currentUserName });
    await refresh();
    setItemToRename(null);
  };

  const handleMoveConfirm = async (targetFolderId?: string) => {
    if (!itemToMove) return;
    await SupabaseFileStorage.updateFile(itemToMove, { parentId: targetFolderId, modifiedBy: currentUserName });
    await refresh();
    setItemToMove(null);
    setSelectedItems(new Set());
  };

  const handleUpload = async (uploadedFiles: File[]) => {
    if (!currentWorkspace || !user) return;

    const targetFolderId = currentFolderId ?? projectRootId ?? undefined;
    if (!targetFolderId) {
      toast.error('Project folder is not ready. Please wait a moment and try again.');
      return;
    }

    const initial: UploadProgress[] = uploadedFiles.map((f, i) => ({
      id: `${Date.now()}-${i}`,
      fileName: f.name,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploadProgress(initial);

    for (let i = 0; i < uploadedFiles.length; i++) {
      const file = uploadedFiles[i];
      const pid = initial[i].id;
      try {
        setUploadProgress(prev => prev.map(p => p.id === pid ? { ...p, status: 'uploading', progress: 20 } : p));

        const res = await uploadToR2({
          fileName: file.name,
          file,
          folder: `workspace_files/${currentWorkspace.id}/projects/${projectId}`,
        });

        setUploadProgress(prev => prev.map(p => p.id === pid ? { ...p, progress: 70 } : p));

        await SupabaseFileStorage.addFile(currentWorkspace.id, user.id, {
          name: file.name,
          type: 'file',
          size: file.size,
          mimeType: file.type,
          parentId: targetFolderId,
          starred: false,
          shared: false,
          url: res.publicUrl,
          trashed: false,
          ownerName: currentUserName,
          ownerAvatarUrl: currentUserAvatarUrl || undefined,
          modifiedBy: currentUserName,
        });

        setUploadProgress(prev => prev.map(p => p.id === pid ? { ...p, status: 'success', progress: 100 } : p));
      } catch (err) {
        console.error('Upload failed:', file.name, err);
        setUploadProgress(prev => prev.map(p => p.id === pid ? { ...p, status: 'error', progress: 0, error: 'Upload failed' } : p));
      }
    }

    await refresh();
  };

  const handleCreateFolder = async (name: string) => {
    if (!currentWorkspace || !user) return;

    const targetFolderId = currentFolderId ?? projectRootId ?? undefined;
    if (!targetFolderId) {
      toast.error('Project folder is not ready. Please wait a moment and try again.');
      return;
    }

    await SupabaseFileStorage.createFolder(
      currentWorkspace.id,
      user.id,
      name,
      targetFolderId,
      currentUserName,
      currentUserAvatarUrl
    );
    await refresh();
  };

  const handleItemSelect = (id: string, selected: boolean) => {
    if (!selectionMode) setSelectionMode(true);
    setSelectedItems(prev => {
      const s = new Set(prev);
      selected ? s.add(id) : s.delete(id);
      return s;
    });
  };

  const handleSelectAll = () => {
    if (selectionMode) setSelectedItems(new Set(currentFiles.map(f => f.id)));
    else setSelectionMode(true);
  };

  const handleDeselectAll = () => { setSelectionMode(false); setSelectedItems(new Set()); };

  const handleBulkDelete = async () => {
    for (const id of selectedItems) await SupabaseFileStorage.deleteFile(id);
    await refresh();
    setSelectedItems(new Set());
  };

  const handleBulkMoveTo = () => {
    if (selectedItems.size > 0) {
      setItemToMove(Array.from(selectedItems).join(','));
      setShowMoveModal(true);
    }
  };

  const handleBulkMoveConfirm = async (targetFolderId?: string) => {
    if (!itemToMove) return;
    const ids = itemToMove.includes(',') ? itemToMove.split(',').filter(Boolean) : [itemToMove];
    for (const id of ids) await SupabaseFileStorage.updateFile(id, { parentId: targetFolderId, modifiedBy: currentUserName });
    await refresh();
    setItemToMove(null);
    setSelectedItems(new Set());
  };

  const handleFilePreview = (file: FileItem) => { setPreviewFile(file); setShowPreviewModal(true); };

  // ── Drag-and-drop ────────────────────────────────────────────────────────
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (!isReadOnly && e.dataTransfer.types.includes('Files')) setIsDraggingOver(true);
  };
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    if (e.currentTarget === e.target) setIsDraggingOver(false);
  };
  const handleDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); };
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation();
    setIsDraggingOver(false);
    if (isReadOnly) return;
    const droppedFiles = Array.from(e.dataTransfer.items)
      .filter(item => item.kind === 'file')
      .map(item => item.getAsFile())
      .filter(Boolean) as File[];
    if (droppedFiles.length > 0) await handleUpload(droppedFiles);
  };

  if (!currentWorkspace) return <LoadingSpinner fullPage />;

  return (
    <div
      className="flex flex-col min-h-[500px] relative"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDraggingOver && !isReadOnly && (
        <div className="absolute inset-0 z-50 bg-workspace-accent/10 backdrop-blur-sm border-4 border-dashed border-workspace-accent flex items-center justify-center rounded-xl">
          <div className="bg-card/95 rounded-2xl shadow-2xl p-12 text-center border-2 border-workspace-accent">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-workspace-accent/20 flex items-center justify-center">
              <svg className="w-10 h-10 text-workspace-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
            </div>
            <h3 className="text-xl font-semibold text-foreground mb-1">Drop files here</h3>
            <p className="text-muted-foreground text-sm">Release to upload to {projectName}</p>
          </div>
        </div>
      )}

      {/* Single padded content area — same px/py as Tasks tab */}
      <div className="px-6 md:px-10 pt-6 pb-6">

        {/* Read-only banner */}
        {isReadOnly && (
          <div className="flex items-center gap-2 py-2.5 px-4 mb-4 bg-muted/60 rounded-lg border border-border text-xs text-muted-foreground">
            <Lock className="w-3.5 h-3.5 shrink-0" />
            <span>You have read-only access to project files. Contact your project manager to upload or delete files.</span>
          </div>
        )}

        <Toolbar
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
          onUpload={(isReadOnly || !projectRootId) ? undefined : () => setShowUploadModal(true)}
          onCreateFolder={(isReadOnly || !projectRootId) ? undefined : () => setShowCreateFolderModal(true)}
          onToggleSidebar={undefined}
          fileTypeFilter={fileTypeFilter}
          onFileTypeFilterChange={setFileTypeFilter}
          selectionMode={selectionMode}
          selectedItemsCount={selectedItems.size}
          onSelectAll={handleSelectAll}
          onDeselectAll={handleDeselectAll}
          isGlobalDarkMode={isGlobalDarkMode}
          noBorder
          breadcrumbSlot={
            <nav className="flex items-center gap-1">
              <button
                onClick={handleGoToRoot}
                className="flex items-center gap-1.5 px-2 py-1 text-muted-foreground hover:text-foreground hover:bg-accent rounded-md transition-colors duration-200"
              >
                <svg className="w-4 h-4 text-workspace-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z" />
                </svg>
                <span className="text-sm font-medium text-workspace-accent">{projectName}</span>
              </button>
              {breadcrumbs.map((item, index) => (
                <React.Fragment key={item.id}>
                  <svg className="w-3.5 h-3.5 text-muted-foreground/50" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <button
                    onClick={() => setCurrentFolderId(item.id)}
                    className={`px-2 py-1 text-sm font-medium rounded-md transition-colors duration-200 ${
                      index === breadcrumbs.length - 1
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                    }`}
                  >
                    {item.name}
                  </button>
                </React.Fragment>
              ))}
            </nav>
          }
        />

        {/* Mobile view controls */}
        <div className="lg:hidden mt-3">
          <ViewControls
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            fileTypeFilter={fileTypeFilter}
            onFileTypeFilterChange={setFileTypeFilter}
            selectionMode={selectionMode}
            onSelectAll={handleSelectAll}
            onDeselectAll={handleDeselectAll}
            isGlobalDarkMode={isGlobalDarkMode}
          />
        </div>

        {/* File grid — same container, naturally aligned */}
        <div className="mt-4">
        <FileGrid
          files={currentFiles}
          viewMode={viewMode}
          isTrashView={false}
          selectionMode={selectionMode}
          selectedItems={selectedItems}
          onItemSelect={handleItemSelect}
          onFolderOpen={handleFolderOpen}
          onFileDelete={isReadOnly ? undefined : handleFileDelete}
          onFileRestore={isReadOnly ? undefined : handleFileRestore}
          onPermanentDelete={isReadOnly ? undefined : handlePermanentDelete}
          onToggleStar={isReadOnly ? undefined : handleToggleStar}
          onShare={isReadOnly ? undefined : handleShare}
          onDuplicate={isReadOnly ? undefined : handleDuplicate}
          onMoveTo={isReadOnly ? undefined : handleMoveTo}
          onRename={isReadOnly ? undefined : handleRename}
          onFilePreview={handleFilePreview}
          onFileDetails={handleFileDetails}
          onFolderDetails={handleFolderDetails}
          isGlobalDarkMode={isGlobalDarkMode}
          currentUserName={currentUserName}
          currentUserAvatarUrl={currentUserAvatarUrl}
          currentUserId={profile?.id}
          isLoading={loading}
        />
        </div>{/* end mt-4 file grid wrapper */}
      </div>{/* end px-6 md:px-10 py-6 */}

      {/* Bulk actions */}
      {selectedItems.size > 0 && !isReadOnly && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-card rounded-lg shadow-lg border border-border px-6 py-4 flex items-center gap-4 z-50">
          <span className="text-sm font-medium text-foreground">
            {selectedItems.size} item{selectedItems.size > 1 ? 's' : ''} selected
          </span>
          <div className="flex items-center gap-2">
            <button onClick={handleBulkMoveTo} className="px-3 py-1.5 bg-workspace-accent hover:bg-workspace-accent-hover text-white text-sm rounded-md transition-colors">Move To</button>
            <button onClick={handleBulkDelete} className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white text-sm rounded-md transition-colors">Move to Trash</button>
            <button onClick={handleDeselectAll} className="px-3 py-1.5 bg-muted hover:bg-accent text-foreground text-sm rounded-md transition-colors">Cancel</button>
          </div>
        </div>
      )}

      {/* Modals */}
      {!isReadOnly && (
        <>
          <UploadModal isOpen={showUploadModal} onClose={() => setShowUploadModal(false)} onUpload={handleUpload} />
          <CreateFolderModal isOpen={showCreateFolderModal} onClose={() => setShowCreateFolderModal(false)} onCreate={handleCreateFolder} isGlobalDarkMode={isGlobalDarkMode} />
          <FolderSelectionModal
            isOpen={showMoveModal}
            onClose={() => { setShowMoveModal(false); setItemToMove(null); }}
            onSelect={itemToMove?.includes(',') ? handleBulkMoveConfirm : handleMoveConfirm}
            files={allFiles}
            currentItemId={itemToMove || ''}
            title={itemToMove?.includes(',') ? `Move ${itemToMove.split(',').length} items to Folder` : 'Move to Folder'}
          />
          <RenameModal
            isOpen={showRenameModal}
            onClose={() => { setShowRenameModal(false); setItemToRename(null); }}
            onRename={handleRenameConfirm}
            currentName={itemToRename?.name || ''}
            itemType={itemToRename?.type || 'file'}
            isGlobalDarkMode={isGlobalDarkMode}
          />
        </>
      )}

      <FilePreviewModal
        isOpen={showPreviewModal}
        onClose={() => { setShowPreviewModal(false); setPreviewFile(null); }}
        file={previewFile}
        onToggleStar={isReadOnly ? undefined : handleToggleStar}
        onShare={isReadOnly ? undefined : handleShare}
        isGlobalDarkMode={isGlobalDarkMode}
      />

      <DetailsModal
        isOpen={showDetailsModal}
        onClose={() => { setShowDetailsModal(false); setItemDetails(null); }}
        item={itemDetails}
        isGlobalDarkMode={isGlobalDarkMode}
        currentUserName={currentUserName}
      />

      {uploadProgress.length > 0 && (
        <UploadProgressPopup
          uploads={uploadProgress}
          onClose={() => setUploadProgress([])}
          onCancel={() => {}}
        />
      )}
    </div>
  );
};

export default ProjectFileTab;
